import { useEffect, useMemo, useRef, useState } from "preact/hooks";

import { DataTable, type DataTableColumn, type DataTableFilterChip } from "../components/DataTable";
import { useAnimals } from "../hooks/useAnimals";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import {
  useCompletionRateReport,
  useHealthLogReport,
  useOverdueAgingReport,
  useWorkloadReport,
} from "../hooks/useReports";
import { HELP_COPY } from "../lib/helpCopy";
import { monthDayLabel } from "../lib/recordsCopy";
import {
  defaultReportRange,
  flattenOverdueRows,
  HEALTH_LOG_ALL_ANIMALS_OPTION,
  HEALTH_LOG_ANIMAL_FILTER_LABEL,
  isValidReportRange,
  type OverdueFlatRow,
  OVERDUE_AGE_BUCKET_ORDER,
  overdueBucketSortIndex,
  REPORT_DATE_RANGE_ERROR,
  REPORT_EMPTY_MESSAGES,
  reportEmptyMessage,
  REPORT_PANEL_SUBHEADINGS,
  REPORT_TABS,
  type ReportTab,
  REPORTS_PAGE_SUBHEADING,
} from "../lib/reportsCopy";
import type { CompletionRateRowOut, HealthLogRowOut, WorkloadRowOut } from "../types";

export interface ReportsScreenProps {
  /** `app.tsx` guards the `/reports` ROUTE itself (an unauthorized deep
   * link redirects to `/more` before this component ever mounts), so this
   * is always `true` in practice — still threaded through explicitly
   * (rather than assumed) so each report hook's own `enabled` flag stays
   * the single source of truth for whether its `GET /reports/*` call ever
   * fires, matching `ProjectsScreen`'s exact precedent. */
  canHandOutWork: boolean;
  onBack: () => void;
}

interface DateRangeToolbarProps {
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onApply: () => void;
  error: string | null;
}

/** §5.2's shared native date-range toolbar — two `<input type="date">` +
 * an "Apply" button (never live-refetch-on-every-keystroke). Reused by
 * three of the four report panels (Overdue's own panel never renders this
 * at all — §5.2's own SA-ruled ommission). */
function ReportDateRangeToolbar({
  from,
  to,
  onFromChange,
  onToChange,
  onApply,
  error,
}: DateRangeToolbarProps) {
  return (
    <div class="report-toolbar">
      <div class="report-date-field">
        <label for="report-from">From</label>
        <input
          id="report-from"
          type="date"
          class="pick-input"
          value={from}
          onInput={(e) => onFromChange((e.target as HTMLInputElement).value)}
        />
      </div>
      <div class="report-date-field">
        <label for="report-to">To</label>
        <input
          id="report-to"
          type="date"
          class="pick-input"
          value={to}
          onInput={(e) => onToChange((e.target as HTMLInputElement).value)}
        />
      </div>
      <button type="button" class="secondary-btn" onClick={onApply}>
        Apply
      </button>
      {error ? (
        <p class="error-hint" role="alert" style={{ width: "100%", margin: 0 }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function PanelHeading({ text }: { text: string }) {
  const ref = useRef<HTMLHeadingElement | null>(null);
  // §5.6 — tab switches move focus to the newly-active panel's own
  // subheading-equivalent `<h2 tabindex="-1">`, not a full re-announcement
  // of the page's own `<h1>` ("Reports"). Each panel component below is
  // only ever mounted while its own tab is active (see `ReportsScreen`'s
  // conditional render), so a fresh mount happens on every tab switch —
  // the same "mount IS the mechanism" posture `useFocusHeadingOnMount`
  // already relies on everywhere else in this app.
  useFocusHeadingOnMount(ref);
  return (
    <h2 ref={ref} tabIndex={-1} class="view-subheading">
      {text}
    </h2>
  );
}

function useReportRangeState() {
  const [range, setRange] = useState(() => defaultReportRange());
  const [error, setError] = useState<string | null>(null);
  return { range, setRange, error, setError };
}

function completionRateColumns(): Array<DataTableColumn<CompletionRateRowOut>> {
  return [
    {
      key: "person",
      label: "Person",
      sortable: true,
      getSortValue: (row) => row.person_name,
      getCsvValue: (row) => row.person_name,
    },
    {
      key: "week",
      label: "Week of",
      sortable: true,
      getSortValue: (row) => row.week_start,
      getCsvValue: (row) => monthDayLabel(row.week_start),
    },
    {
      key: "chores_done",
      label: "Chores done",
      sortable: true,
      getSortValue: (row) => row.cadence_completions,
      getCsvValue: (row) => String(row.cadence_completions),
    },
    {
      key: "tasks_done",
      label: "Tasks done",
      sortable: true,
      getSortValue: (row) => row.assignment_completions,
      getCsvValue: (row) => String(row.assignment_completions),
    },
    {
      key: "total",
      label: "Total",
      sortable: true,
      getSortValue: (row) => row.total,
      getCsvValue: (row) => String(row.total),
    },
  ];
}

function defaultCompletionRateCompare(a: CompletionRateRowOut, b: CompletionRateRowOut): number {
  const nameDiff = a.person_name.localeCompare(b.person_name);
  if (nameDiff !== 0) return nameDiff;
  return a.week_start.localeCompare(b.week_start);
}

function CompletionRatePanel({ enabled }: { enabled: boolean }) {
  const report = useCompletionRateReport(enabled);
  const { range, setRange, error, setError } = useReportRangeState();
  const columns = useMemo(() => completionRateColumns(), []);

  useEffect(() => {
    void report.load(range.from, range.to);
    // Load once on activation with the default range — subsequent loads
    // only happen via the explicit "Apply" click below (§5.2).
  }, []);

  function handleApply() {
    if (!isValidReportRange(range.from, range.to)) {
      setError(REPORT_DATE_RANGE_ERROR);
      return;
    }
    setError(null);
    void report.load(range.from, range.to);
  }

  return (
    <section class="report-panel">
      <PanelHeading text={REPORT_PANEL_SUBHEADINGS.completions} />
      <ReportDateRangeToolbar
        from={range.from}
        to={range.to}
        onFromChange={(v) => setRange((r) => ({ ...r, from: v }))}
        onToChange={(v) => setRange((r) => ({ ...r, to: v }))}
        onApply={handleApply}
        error={error}
      />
      <DataTable
        captionBase="Completion rate"
        columns={columns}
        rows={report.data?.rows ?? []}
        getRowId={(row) => `${row.person_id}:${row.week_start}`}
        loading={report.loading}
        loadingMessage="Loading…"
        emptyMessage={reportEmptyMessage("completions", report.data?.rows.length ?? 0, range)}
        primaryColumnKey="person"
        defaultSortLabel="person ascending, then week ascending"
        defaultCompare={defaultCompletionRateCompare}
        screenSlug="reports-completions"
      />
      {report.error ? (
        <p class="error-hint" role="alert">
          {report.error}
        </p>
      ) : null}
    </section>
  );
}

function overdueColumns(): Array<DataTableColumn<OverdueFlatRow>> {
  return [
    {
      key: "chore",
      label: "Chore",
      sortable: true,
      getSortValue: (row) => row.title,
      getCsvValue: (row) => row.title,
    },
    {
      key: "area",
      label: "Area",
      sortable: true,
      getSortValue: (row) => row.area ?? "",
      getCsvValue: (row) => row.area ?? "",
    },
    {
      key: "overdue_since",
      label: "Overdue since",
      sortable: true,
      getSortValue: (row) => row.overdueSince,
      getCsvValue: (row) => monthDayLabel(row.overdueSince),
    },
    {
      key: "age_bucket",
      label: "How overdue",
      sortable: true,
      getSortValue: (row) => overdueBucketSortIndex(row.bucket),
      getCsvValue: (row) => row.bucket,
    },
  ];
}

function overdueFilterChips(rows: OverdueFlatRow[]): Array<DataTableFilterChip<OverdueFlatRow>> {
  const present = new Set(rows.map((r) => r.bucket));
  return OVERDUE_AGE_BUCKET_ORDER.filter((bucket) => present.has(bucket)).map((bucket) => ({
    key: `bucket:${bucket}`,
    label: bucket,
    predicate: (row: OverdueFlatRow) => row.bucket === bucket,
  }));
}

function defaultOverdueCompare(a: OverdueFlatRow, b: OverdueFlatRow): number {
  return overdueBucketSortIndex(a.bucket) - overdueBucketSortIndex(b.bucket);
}

function OverduePanel({ enabled }: { enabled: boolean }) {
  const report = useOverdueAgingReport(enabled);
  const rows = useMemo(() => flattenOverdueRows(report.data?.rows ?? []), [report.data]);
  const columns = useMemo(() => overdueColumns(), []);
  const filterChips = useMemo(() => overdueFilterChips(rows), [rows]);

  useEffect(() => {
    void report.load();
  }, []);

  return (
    <section class="report-panel">
      <PanelHeading text={REPORT_PANEL_SUBHEADINGS.overdue} />
      {/* §5.2 — SA-ruled: this report is point-in-time and takes no
       * `from`/`to` at all, so the shared date-range toolbar doesn't
       * render here (there's no parameter for it to control). The
       * subheading above IS the explanation (R-B0b-2). Sprint-019 E5b
       * (audit row #10) adds an explicit inline sentence too — a
       * manager reasonably expects the SAME date-range toolbar every
       * other report tab has, and its total absence here (not merely a
       * disabled state) is easy to read as broken rather than
       * intentional. */}
      <p class="footer-hint">{HELP_COPY.reportsOverdueNoRange.content}</p>
      <DataTable
        captionBase="Overdue"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.key}
        loading={report.loading}
        loadingMessage="Loading…"
        emptyMessage={REPORT_EMPTY_MESSAGES.overdue}
        primaryColumnKey="chore"
        searchLabel="Search by chore title"
        getSearchValue={(row) => row.title}
        filterChips={filterChips}
        filterGroupLabel="Filter by how overdue"
        defaultSortLabel="how overdue, oldest first"
        defaultCompare={defaultOverdueCompare}
        screenSlug="reports-overdue"
      />
      {report.error ? (
        <p class="error-hint" role="alert">
          {report.error}
        </p>
      ) : null}
    </section>
  );
}

/** `HealthLogRowOut` carries no server-issued row id — this build derives
 * one client-side (position within the currently-loaded page, stable for
 * the life of that response) so `DataTable`'s `getRowId` never collides on
 * two same-animal, same-day notes. */
type HealthLogRow = HealthLogRowOut & { rowKey: string };

function withHealthLogRowKeys(rows: HealthLogRowOut[]): HealthLogRow[] {
  return rows.map((row, index) => ({
    ...row,
    rowKey: `${row.animal_name}:${row.event_date}:${index}`,
  }));
}

function healthLogColumns(): Array<DataTableColumn<HealthLogRow>> {
  return [
    {
      key: "animal",
      label: "Animal",
      sortable: true,
      getSortValue: (row) => row.animal_name,
      getCsvValue: (row) => row.animal_name,
    },
    {
      key: "kind",
      label: "Kind",
      sortable: true,
      getSortValue: (row) => row.event_type_label,
      getCsvValue: (row) => row.event_type_label,
    },
    {
      key: "date",
      label: "Date",
      sortable: true,
      getSortValue: (row) => row.event_date,
      getCsvValue: (row) => monthDayLabel(row.event_date),
    },
    {
      key: "note",
      label: "Note",
      getCsvValue: (row) => row.note ?? "",
    },
    {
      key: "logged_by",
      label: "Logged by",
      sortable: true,
      getSortValue: (row) => row.recorded_by_name,
      getCsvValue: (row) => row.recorded_by_name,
    },
  ];
}

function defaultHealthLogCompare(a: HealthLogRow, b: HealthLogRow): number {
  return b.event_date.localeCompare(a.event_date);
}

function HealthLogPanel({ enabled }: { enabled: boolean }) {
  const report = useHealthLogReport(enabled);
  const animals = useAnimals(enabled);
  const { range, setRange, error, setError } = useReportRangeState();
  const [animalId, setAnimalId] = useState<string>("");
  const columns = useMemo(() => healthLogColumns(), []);
  const rows = useMemo(() => withHealthLogRowKeys(report.data?.rows ?? []), [report.data]);

  useEffect(() => {
    void report.load(range.from, range.to, animalId || null);
  }, []);

  function handleApply() {
    if (!isValidReportRange(range.from, range.to)) {
      setError(REPORT_DATE_RANGE_ERROR);
      return;
    }
    setError(null);
    void report.load(range.from, range.to, animalId || null);
  }

  function handleAnimalChange(value: string) {
    setAnimalId(value);
    void report.load(range.from, range.to, value || null);
  }

  const activeAnimals = (animals.animals ?? []).filter((a) => a.active);

  return (
    <section class="report-panel">
      <PanelHeading text={REPORT_PANEL_SUBHEADINGS["health-log"]} />
      <div class="report-date-field" style={{ marginBottom: "10px" }}>
        <label for="report-health-animal">{HEALTH_LOG_ANIMAL_FILTER_LABEL}</label>
        <select
          id="report-health-animal"
          class="pick-input"
          value={animalId}
          onChange={(e) => handleAnimalChange((e.target as HTMLSelectElement).value)}
        >
          <option value="">{HEALTH_LOG_ALL_ANIMALS_OPTION}</option>
          {activeAnimals.map((animal) => (
            <option key={animal.id} value={animal.id}>
              {animal.name}
            </option>
          ))}
        </select>
      </div>
      <ReportDateRangeToolbar
        from={range.from}
        to={range.to}
        onFromChange={(v) => setRange((r) => ({ ...r, from: v }))}
        onToChange={(v) => setRange((r) => ({ ...r, to: v }))}
        onApply={handleApply}
        error={error}
      />
      <DataTable
        captionBase="Health log"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.rowKey}
        loading={report.loading}
        loadingMessage="Loading…"
        emptyMessage={reportEmptyMessage("health-log", rows.length, range)}
        primaryColumnKey="animal"
        searchLabel="Search by animal or note"
        getSearchValue={(row) => `${row.animal_name} ${row.note ?? ""}`}
        defaultSortLabel="date descending"
        defaultCompare={defaultHealthLogCompare}
        screenSlug="reports-health-log"
      />
      {report.error ? (
        <p class="error-hint" role="alert">
          {report.error}
        </p>
      ) : null}
    </section>
  );
}

function workloadColumns(): Array<DataTableColumn<WorkloadRowOut>> {
  return [
    {
      key: "area",
      label: "Area",
      sortable: true,
      getSortValue: (row) => row.area ?? "",
      getCsvValue: (row) => row.area ?? "",
    },
    {
      key: "scheduled",
      label: "Scheduled",
      sortable: true,
      getSortValue: (row) => row.scheduled_count,
      getCsvValue: (row) => String(row.scheduled_count),
    },
    {
      key: "completed",
      label: "Completed",
      sortable: true,
      getSortValue: (row) => row.completed_count,
      getCsvValue: (row) => String(row.completed_count),
    },
    {
      key: "completion_pct",
      label: "Completion %",
      sortable: true,
      getSortValue: (row) => row.completion_pct,
      getCsvValue: (row) => `${Math.round(row.completion_pct)}%`,
    },
  ];
}

function defaultWorkloadCompare(a: WorkloadRowOut, b: WorkloadRowOut): number {
  return (a.area ?? "").localeCompare(b.area ?? "");
}

function WorkloadPanel({ enabled }: { enabled: boolean }) {
  const report = useWorkloadReport(enabled);
  const { range, setRange, error, setError } = useReportRangeState();
  const columns = useMemo(() => workloadColumns(), []);

  useEffect(() => {
    void report.load(range.from, range.to);
  }, []);

  function handleApply() {
    if (!isValidReportRange(range.from, range.to)) {
      setError(REPORT_DATE_RANGE_ERROR);
      return;
    }
    setError(null);
    void report.load(range.from, range.to);
  }

  return (
    <section class="report-panel">
      <PanelHeading text={REPORT_PANEL_SUBHEADINGS.workload} />
      <ReportDateRangeToolbar
        from={range.from}
        to={range.to}
        onFromChange={(v) => setRange((r) => ({ ...r, from: v }))}
        onToChange={(v) => setRange((r) => ({ ...r, to: v }))}
        onApply={handleApply}
        error={error}
      />
      <DataTable
        captionBase="Workload"
        columns={columns}
        rows={report.data?.rows ?? []}
        getRowId={(row) => row.area ?? "unassigned"}
        loading={report.loading}
        loadingMessage="Loading…"
        emptyMessage={reportEmptyMessage("workload", report.data?.rows.length ?? 0, range)}
        primaryColumnKey="area"
        defaultSortLabel="area ascending"
        defaultCompare={defaultWorkloadCompare}
        screenSlug="reports-workload"
      />
      {report.error ? (
        <p class="error-hint" role="alert">
          {report.error}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Sprint-009 B4 — `/reports` (design-phase-b.md §5, ADR-002 Amendment 5
 * decision 6). One page, four in-page tabs, reusing `AdminView`'s
 * `.admin-subtabs` chrome verbatim (§5.1) — unlike `AdminView`'s own
 * People/Chores subtabs, this strip is NEVER removed at desktop (there is
 * no sidebar row per report tab, only one "Reports" row for the whole
 * page), so it renders at every viewport.
 *
 * **Flagged deviation** (see PR body): `DataTable` (`components/
 * DataTable.tsx`) is documented as "never mounted below 1024px," with each
 * of Phase A's FOUR applied surfaces (Team/My list/People/Chores) falling
 * back to an already-existing, purpose-built phone list component at that
 * breakpoint. Reports has no such pre-existing phone list to fall back to
 * (it's new this sprint) and design-phase-b.md §5 specifies no phone-
 * specific layout for it — the spec's own §1.2 routing table nonetheless
 * makes `/reports` phone-reachable (a `More` landing row). Rather than
 * invent a new, unspecified phone-only report component, this build
 * mounts the SAME `DataTable` at every viewport for Reports specifically,
 * with one small, additive CSS change (`.data-table-wrap { overflow-x:
 * auto }`) so a narrow viewport scrolls horizontally instead of breaking
 * layout — the existing `DataTable` component itself is untouched.
 */
export function ReportsScreen({ canHandOutWork, onBack }: ReportsScreenProps) {
  const isDesktop = useIsDesktop();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useFocusHeadingOnMount(headingRef);
  const [tab, setTab] = useState<ReportTab>("completions");

  return (
    <section class="view" aria-label="Reports">
      {isDesktop ? (
        <header class="page-header">
          <h1 ref={headingRef} tabIndex={-1} class="view-heading">
            Reports
          </h1>
          <p class="view-subheading">{REPORTS_PAGE_SUBHEADING}</p>
        </header>
      ) : (
        <>
          <div class="more-subheader">
            <button type="button" class="icon-btn" aria-label="Back to More" onClick={onBack}>
              ‹
            </button>
            <h1 ref={headingRef} tabIndex={-1} class="view-heading">
              Reports
            </h1>
          </div>
          <p class="view-subheading">{REPORTS_PAGE_SUBHEADING}</p>
        </>
      )}

      <div class="admin-subtabs" role="tablist" aria-label="Report sections">
        {REPORT_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            id={`report-subtab-${t.key}`}
            role="tab"
            aria-selected={tab === t.key}
            aria-controls={`report-subpanel-${t.key}`}
            class={`admin-subtab${tab === t.key ? " is-active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div id={`report-subpanel-${tab}`} role="tabpanel" aria-labelledby={`report-subtab-${tab}`}>
        {tab === "completions" ? (
          <CompletionRatePanel enabled={canHandOutWork} />
        ) : tab === "overdue" ? (
          <OverduePanel enabled={canHandOutWork} />
        ) : tab === "health-log" ? (
          <HealthLogPanel enabled={canHandOutWork} />
        ) : (
          <WorkloadPanel enabled={canHandOutWork} />
        )}
      </div>
    </section>
  );
}
